import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import style from "./rule-editable-value.module.css";
import { Floater } from "frontend/ui-components/floaters/floater";
import { useOnClickOutside } from "usehooks-ts";
import classNames from "classnames";
import { getPathPrefix } from "utils/getPathPrefix";
import { PortalID } from "frontend/modal/portal-types";

interface RuleEditableContainer<T> {
  value?: T;
  placeholder: string;
  getTitle: (value: T) => ReactNode;
  onChange: (value: T) => void;
  editable: boolean;
  variant?: "field" | "item";
  name?: string;
  isMissing?: boolean;
}

interface RuleEditableDropdown<T> extends RuleEditableContainer<T> {
  options: T[];
}

interface RuleEditableText {
  value?: string;
  placeholder: string;
  onChange: (value: string) => void;
  editable: boolean;
  name?: string;
}

function RuleEditableContainer<T>({
  value,
  placeholder,
  getTitle,
  children,
  editable,
  variant = "field",
  name,
  isMissing = false,
}: RuleEditableContainer<T> & { children: any }) {
  const ref = useRef<HTMLDivElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // @ts-expect-error usehooks-ts clais to only accept mousedown or mouseup but click works fine.
  useOnClickOutside(ref, () => setDropdownOpen(false), "click");

  const portal = document.getElementById(PortalID.Floaters);

  function renderDropdown() {
    const element = ref.current ? (
      <Floater relativeTo={ref} boundary="stage-container" side="bottom">
        {children}
      </Floater>
    ) : null;

    return portal ? ReactDOM.createPortal(element, portal) : element;
  }

  if (typeof children === "function") {
    // allow children to be a function that returns the dropdown content
    children = children(setDropdownOpen);
  }

  function renderChevron() {
    return (
      <svg width="8" height="5" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.99982 1L3 3.05226" stroke="currentColor" strokeLinecap="round" />
        <path d="M1 1L2.99982 3.05226" stroke="currentColor" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <div
      className={classNames(style.container, {
        [style.field]: variant === "field",
        [style.item]: variant === "item",
      })}
      ref={ref}
      style={{ zIndex: dropdownOpen ? 11 : undefined }}
    >
      <div
        className={classNames(style.value, {
          [style.disabled]: !editable,
          [style.field]: variant === "field",
          [style.item]: variant === "item",
          [style.placeholder]: !value,
          [style.missing]: isMissing,
        })}
        data-name={name}
        onClick={() => setDropdownOpen(editable && !dropdownOpen)}
      >
        <span>{value ? getTitle(value) : placeholder}</span>
        {variant === "item" && renderChevron()}
      </div>
      {dropdownOpen && renderDropdown()}
    </div>
  );
}

export function RuleEditableDropdown<T>(props: RuleEditableDropdown<T>) {
  const { value, options, getTitle, onChange, variant } = props;

  function renderCheckmark() {
    return (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 4.41406L3.5 6.91406L9 1.41406" stroke="#4A90FC" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    );
  }

  function renderOption(option: T, setDropdownOpen: any) {
    const isSelected = value === option;
    return (
      <div
        key={JSON.stringify(option)}
        className={classNames(style.option, { [style.selected]: isSelected })}
        onClick={(e) => {
          e.stopPropagation();
          onChange(option);
          setDropdownOpen(false);
        }}
      >
        <span>{getTitle(option)}</span>
        {isSelected && renderCheckmark()}
      </div>
    );
  }

  return (
    <RuleEditableContainer {...props}>
      {(setDropdownOpen: any) => (
        <>
          <div className={classNames(style.dropdown, { [style.item]: variant === "item" })}>
            {options.length === 0 ? (
              <div className={style.noOptions}>No options</div>
            ) : (
              options.map((id) => renderOption(id, setDropdownOpen))
            )}
          </div>
        </>
      )}
    </RuleEditableContainer>
  );
}

export function RuleEditableText({ value, placeholder, onChange, editable, name }: RuleEditableText) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => endEditing(true));

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const endEditing = useCallback(
    (changeValue: boolean) => {
      if (changeValue) {
        onChange(inputValue ?? "");
      } else {
        setInputValue(value);
      }
      setIsEditing(false);
    },
    [inputValue, value]
  );

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === "Enter" || e.key === "Tab") {
        endEditing(true);
      } else if (e.key === "Escape") {
        endEditing(false);
      }
    },
    [endEditing]
  );

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, isEditing]);

  function renderInput() {
    return (
      <input
        className={style.input}
        autoFocus
        type="text"
        name={"rule-editable-text"}
        data-name={name}
        size={Math.min(Math.max(inputValue?.length ?? 0, 5), 20)}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onFocus={(e) => e.target.select()}
      />
    );
  }

  function renderValue() {
    return (
      <div
        className={classNames(style.value, {
          [style.disabled]: !editable,
          [style.placeholder]: !value,
        })}
        data-name={name}
        onClick={() => setIsEditing(editable && !isEditing)}
        style={{ zIndex: isEditing ? 11 : 10 }}
      >
        <span>{value || placeholder}</span>
      </div>
    );
  }

  return (
    <div className={style.container} ref={ref}>
      {!isEditing && renderValue()}
      {isEditing && renderInput()}
    </div>
  );
}

enum RuleIcon {
  arrowUp = "arrow-up",
  arrowDown = "arrow-down",
  greenCheckmark = "green-checkmark",
  redX = "red-x",
}

export function RuleIconPicker({
  value,
  onChange,
  editable,
}: {
  value?: string;
  onChange: (value: string) => void;
  editable: boolean;
}) {
  const icons = Object.values(RuleIcon);

  return (
    <RuleEditableDropdown
      value={value}
      placeholder="Icon"
      options={icons}
      getTitle={(icon) => <img alt="icon" src={getPathPrefix(`/images/${icon}.svg`)} />}
      onChange={onChange}
      editable={editable}
    />
  );
}

interface RuleCategorizedDropdownProps<T> extends RuleEditableContainer<T> {
  options: { category: string; option: T }[];
  categories: { id: string; title: string; icon?: ReactNode }[];
  valueMissing?: boolean;
}

export function RuleCategorizedDropdown<T>(props: RuleCategorizedDropdownProps<T>) {
  const { value, options, getTitle, onChange, variant, categories, valueMissing } = props;

  function renderCheckmark() {
    return (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 4.41406L3.5 6.91406L9 1.41406" stroke="#4A90FC" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    );
  }

  function renderOption(option: T, setDropdownOpen: any) {
    const isSelected = value === option;
    return (
      <div
        key={JSON.stringify(option)}
        className={classNames(style.option, { [style.selected]: isSelected })}
        onClick={(e) => {
          e.stopPropagation();
          onChange(option);
          setDropdownOpen(false);
        }}
      >
        <span>{getTitle(option)}</span>
        {isSelected && renderCheckmark()}
      </div>
    );
  }

  const optionsByCategory = useMemo(() => {
    return options.reduce((acc, option) => {
      acc[option.category] = [...(acc[option.category] || []), option.option];
      return acc;
    }, {} as Record<string, T[]>);
  }, [options]);

  return (
    <RuleEditableContainer {...props} isMissing={valueMissing}>
      {(setDropdownOpen: any) => (
        <>
          <div className={classNames(style.dropdown, { [style.item]: variant === "item" })}>
            {categories.map((category) => {
              return (
                <div key={category.id}>
                  <div className={style.categoryTitle}>
                    {category.icon ?? null}
                    {category.title}
                  </div>
                  {!optionsByCategory[category.id] && <div className={style.noOptions}>No options</div>}
                  {optionsByCategory[category.id]?.map((option) => renderOption(option, setDropdownOpen))}
                </div>
              );
            })}
          </div>
        </>
      )}
    </RuleEditableContainer>
  );
}
